import React, { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import {
    useDeleteCustomerByIdMutation,
    useGetCustomerByIdQuery,
    useGetParentCustomerByTenantQuery,
    useLazyGetCustomerByTenantAndLevelQuery,
    useUpdateCustomerMutation,
} from "../customerApi";
import Form from "../../../components/common/form/Form";
import InputField from "../../../components/common/form/InputField";
import { OptionType } from "../../../helpers/utils/StyleUtils";
import SelectField from "../../../components/common/form/SelectField";
import { createOptionListForSelectTag } from "../../../helpers/utils/Utils";
import { useGetEnumsQuery } from "../../../app/globalApi";
import { handleRTKQuery } from "../../../helpers/utils/RtkQueryUtils";
import { skipToken } from "@reduxjs/toolkit/query";
import { useNavigate, useParams } from "react-router-dom";
import { getEnumDisplayNameByConstant } from "../../../helpers/utils/EnumUtils";
import { useRegionByCustomerIdQuery } from "../regionApi";
import { FaTrash } from "react-icons/fa6";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import ModalButton from "../../../components/common/form/ModalButton";
import {
    useUpdatePdfSettingsMutation,
    useGetPdfSettingsByCustomerIdQuery,
    useGetPdfImageByNameQuery,
    useUpdatePdfLogoMutation
} from "../pdfSettingsApi";
import BranchTab from "../branch/BranchTab";
import RegionTab from "../region/RegionTab";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";
import { Tabs } from "react-simple-tabs-component";
import { Routes as AppRoutes } from "../../../routes";
import ButtonTray from "../../../components/common/form/ButtonTray";
import { useUtilDataQuery } from "../../profile/profileApi";
import IconButton from "../../../components/common/form/IconButton";
import AuditableInfoButton from "../../../components/common/form/AuditableInfoButton";
import Button from "../../../components/common/form/Button";
import { objectToQueryParams } from "../../../components/common/datatable/DataTableUtils";
import TextAreaField from "../../../components/common/form/TextAreaField";

const SettingTab = () => {

    let { customerId } = useParams();
    document.title = `Customer Detail`;

    const customerQuery = useGetCustomerByIdQuery(customerId ?? skipToken);
    const pdfSettingQuery = useGetPdfSettingsByCustomerIdQuery(customerId ?? skipToken);
    const parentCustomerByTenantQuery = useGetParentCustomerByTenantQuery(customerQuery?.data?.tenant ?? skipToken);
    const [updateCustomer, updateCustomerApiResponse] = useUpdateCustomerMutation();
    const [updatePdfSettings, updatePdfSettingsApiResponse] = useUpdatePdfSettingsMutation();
    const [updatePdfLogo, updatePdfLogoApiResponse] = useUpdatePdfLogoMutation();
    const [customersApi, customerApiRes] = useLazyGetCustomerByTenantAndLevelQuery();
    const pdfImageByNameIdQuery = useGetPdfImageByNameQuery(pdfSettingQuery?.data?.pdfLogoPath ?? skipToken);
    const { data: enums } = useGetEnumsQuery("");
    const { data: utilData } = useUtilDataQuery("");
    const [customerData, setCustomerData] = useState<any>({});
    const [pdfSettingData, setPdfSettingData] = useState<any>({});
    const [render, setRender] = useState<any>("");
    const [file, setFile] = useState<any>(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showRemoveLogoConfirmation, setShowRemoveLogoConfirmation] = useState(false);
    const regionByCustomerIdQuery = useRegionByCustomerIdQuery(customerData?.parentCustomerReference?.value ?? skipToken)
    const [isReadOnly] = useState(!hasPermission(["CUST-U"]));
    const navigate = useNavigate();
    // Use a union type for state to allow both string and undefined
    const [deleteApi, { isLoading: deleteApiResponse }] = useDeleteCustomerByIdMutation();

    useEffect(() => {
        let customerObj = customerQuery?.data;
        if (customerObj) {
            customersApi({
                tenantName: customerObj.tenant,
                level: customerObj?.customerLevel
            })
            setCustomerData({
                ...customerObj,
                customerType: customerObj.customerType ? {
                    value: customerObj.customerType,
                    label: getEnumDisplayNameByConstant(enums?.["CustomerType"], customerObj.customerType)
                } : null,
                billingType: customerObj.billingType ? {
                    value: customerObj.billingType,
                    label: getEnumDisplayNameByConstant(enums?.["BillingType"], customerObj.billingType)
                } : null,
                region: customerObj?.region ? {
                    value: customerObj?.region?.id,
                    label: customerObj?.region?.name
                } : null,
                parentCustomerReference: customerObj?.parentCustomerReference ? {
                    value: customerObj.parentCustomerReference?.id,
                    label: customerObj.parentCustomerReference?.customerName
                } : null,
                fulfillmentCandidates: customerObj?.fulfillmentCandidates?.map((candidate: any) => ({
                    value: candidate.id,
                    label: candidate.customerName
                })) || [],

            })
        }
    }, [customerQuery?.data]);

    useEffect(() => {
        let settingObj = pdfSettingQuery?.data;

        if (pdfSettingQuery?.data) {
            setPdfSettingData(
                {
                    ...settingObj,
                    pdfHeaderOption: settingObj?.pdfHeaderOption ? {
                        value: settingObj?.pdfHeaderOption,
                        label: getEnumDisplayNameByConstant(enums?.["PdfHeaderOption"], settingObj?.pdfHeaderOption)
                    } : null,
                }
            )
        }
    }, [pdfSettingQuery?.data])

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        let obj = {
            ...customerData,
            customerType: customerData?.customerType?.value,
            billingType: customerData?.billingType?.value,
            contactStrategy: customerData?.contactStrategy,
            region: customerData?.region?.value
                ? regionByCustomerIdQuery?.data?.find((el: any) => el.id === customerData?.region?.value)
                : null,
            parentCustomerReference: customerData?.parentCustomerReference?.value
                ? parentCustomerByTenantQuery?.data?.find((el: any) => el.id === customerData?.parentCustomerReference?.value)
                : null,
            id: customerId,
            fulfillmentCandidates: customerData.fulfillmentCandidates.map((el: any) => ({
                id: el.value
            }))

        };

        let pdfSetting = {
            ...pdfSettingData,
            id: pdfSettingQuery.data?.id,
            pdfHeaderOption: pdfSettingData.pdfHeaderOption?.value,
            pdfLogoPath: pdfSettingData.pdfLogoPath,
            customerId: customerId,
        };

        await handleRTKQuery(async () => {
            await updateCustomer({ id: customerId, requestBody: obj }).unwrap();
            await updatePdfSettings({ pdfSettingObj: pdfSetting }).unwrap();

            if (file != null) {
                try {
                    await updatePdfLogo({ customerId, pdfLogo: file }).unwrap();
                    setFile(null); // only if successful
                } catch (e) {
                    // optional: handle or log the error
                    console.error("Failed to upload logo", e);
                }
            }

            setShowConfirmation(false);
        });
    };

    const handleOnChange = (
        e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | OptionType[] | null }
    ) => {
        const { name, value } = "target" in e ? e.target : e;

        let newValue: any = value;

        // If multi-select, map array of options to array of raw values
        if (Array.isArray(value) && name == "contactStrategy") {
            newValue = value.map((opt) => opt.value);
        }

        setCustomerData((prevData: any) => ({
            ...prevData,
            [name]: newValue,
            ...(name === "parentCustomerReference" && { region: null }),
        }));
    };


    const handlePdfSettingOnChange = (
        e:
          | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          | { name: string; value: any }
      ) => {
        const name = 'target' in e ? e.target.name : e.name;
        const value = 'target' in e ? e.target.value : e.value;
      
        setPdfSettingData((prev:any) => ({
          ...prev,
          [name]: value,
        }));
      };
      

    const handleToggle = (property: string) => () => {
        setCustomerData((prevData: any) => ({
            ...prevData,
            [property]: !prevData[property]
        }));
        setRender(new Date().getMilliseconds());
    };

    const handlePdfSettingToggle = (property: string) => () => {
        setPdfSettingData((prevData: any) => ({
            ...prevData,
            [property]: !prevData[property]
        }));
        setRender(new Date().getMilliseconds());
    };

    const selectedFile = (e: ChangeEvent<HTMLInputElement>) => {
        const uploadFile = e.target.files?.[0] || null;
        setFile(uploadFile);
    }

    const removeCurrentLogo = () => {
        setFile(null)
        setShowRemoveLogoConfirmation(false);
    }

    const handleLogoClick = (imageId: any) => {
        const url = `${process.env.REACT_APP_BASE_URL}/ajax/image/bytes/${imageId}`;
        window.open(url, "_blank"); // Open the URL in a new tab or window
    };

    const showConfirmationModal = (e: any) => {
        e.preventDefault();
        setShowConfirmation(true);
    }
    // Compute bottomTabs using useMemo
    const bottomTabs = useMemo(() => {
        const result: any[] = [];
        if (hasPermission(["BRAN-V"])) {
            result.push({
                label: 'Branches',
                Component: BranchTab

            });
        }
        if (customerData?.customerLevel === "CUSTOMER" && customerData.customerType.value === "WHOLESALE" && customerData.parentCustomer && hasPermission(["CUSRGN-V"])) {
            result.push({
                label: 'Regions',
                Component: RegionTab
            });
        }
        return result;
    }, [customerData.parentCustomer, customerData?.customerLevel]);

    const handleDeleteSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (customerId) {
            await handleRTKQuery(
                () => deleteApi({ customerId }).unwrap(),
                (isSuccess: any) => {
                    if (isSuccess) navigate(AppRoutes.Customers.path)
                }
            );
        }
    };

    return (
        <>

            <div className="justify-between flex absolute top-[-20px] right-0">
                {hasPermission(["USR-R", "USR-V"]) && (
                    <Button
                        btnText="Users List"
                        isLoading={false}
                        type="button"
                        className={"mr-2 h-10"}
                        onClick={() => {
                            const customerNumber = customerData?.customerNumber ?? "";
                            const query = objectToQueryParams({ customerNumber });
                            navigate(
                                {
                                    pathname: "/users",
                                    search: new URLSearchParams({ customerNumber: customerNumber }).toString(),
                                },
                                {
                                    state: {
                                        detail: {
                                            customerNumber: customerNumber,
                                        },
                                    },
                                }
                            );

                        }}
                    />
                )}
                {hasPermission(["CUST-D"]) && <ModalButton
                    triggerButton={
                        <IconButton
                            icon={<FaTrash />}
                            title={"Delete Customers"}
                            hasBackground={true}
                            className="mr-2 h-10"
                        />
                    }
                    modal={
                        <ConfirmationModal
                            show={true}
                            setShow={() => { }}
                            title="Delete Customer"
                            bodyMessage="Are you sure you want to delete this Customer?"
                            onSubmit={handleDeleteSubmit}
                            isLoading={deleteApiResponse}
                        />
                    }
                />
                }
                {hasPermission(["AUDIT-V"]) &&
                    <AuditableInfoButton obj={customerQuery?.data} />
                }
            </div>

            <Form onSubmit={showConfirmationModal} fields={
                <div className="bg-text-0 p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                    <div className="grid grid-cols-12">
                        <InputField
                            name={"customerName"}
                            label="Customer Name"
                            onChange={handleOnChange}
                            value={customerData?.customerName || ""}
                            type="text"
                            placeholder="Enter Customer Name..."
                            required={true}
                            disabled={isReadOnly}
                        />

                        <InputField
                            name={"customerNumber"}
                            label="Customer Number"
                            value={customerData?.customerNumber || ""}
                            type="text"
                            placeholder="Enter Customer Number..."
                            disabled={true}
                        />

                        <InputField
                            name={"customerLevel"}
                            label="Customer Level"
                            value={customerData?.customerLevel || ""}
                            type="text"
                            placeholder="Enter Customer Level..."
                            disabled={true}
                        />


                        {utilData && utilData?.customerLevel === 'SYS' &&
                            <InputField className="form-control" type="text"
                                name="tenant"
                                value={customerData?.tenant}
                                label="Tenant"
                                disabled />
                        }

                        <SelectField
                            placeholder="Select Contact Strategy..."
                            value={
                                customerData?.contactStrategy
                                    ?.map((strategy: string) =>
                                        createOptionListForSelectTag(enums?.["ContactStrategy"], "value", "constant")
                                            .find((option) => option.value === strategy)
                                    )
                                    .filter(Boolean) || []
                            }
                            options={createOptionListForSelectTag(enums?.["ContactStrategy"], "value", "constant")}
                            onChange={(selectedOption) => {
                                handleOnChange({ name: "contactStrategy", value: selectedOption })
                            }}
                            required={true}
                            label="Contact Strategy"
                            isMulti={true}
                            isDisabled={isReadOnly}
                        />

                        <SelectField
                            placeholder='Select Billing Type...'
                            value={customerData?.billingType || null}
                            options={createOptionListForSelectTag(enums?.["BillingType"], "value", "constant")}
                            onChange={(selectedOption) => {
                                handleOnChange({ name: "billingType", value: selectedOption })
                            }}
                            required={true}
                            label={"Billing Type"}
                            isDisabled={isReadOnly}
                        />

                        <SelectField
                            placeholder='Select Customer Type...'
                            value={customerData?.customerType || null}
                            options={createOptionListForSelectTag(enums?.["CustomerType"], "value", "constant")}
                            onChange={(selectedOption) => {
                                handleOnChange({ name: "customerType", value: selectedOption })
                            }}
                            required={true}
                            label={"Customer Type"}
                            isDisabled={isReadOnly}
                        />



                        {customerData?.customerLevel === "CUSTOMER" &&

                            <InputField type="checkbox" name="fulfillmentCustomer"
                                checked={customerData?.fulfillmentCustomer}
                                label="Fulfillment Customer"
                                onClick={handleToggle("fulfillmentCustomer")}
                            />

                        }
                        {
                            customerData?.fulfillmentCustomer === true &&
                            <SelectField
                                placeholder='Select Fulfillment Customer...'
                                value={
                                    customerData?.fulfillmentCandidates
                                }
                                options={createOptionListForSelectTag(
                                    customerApiRes?.data?.filter((el: any) => el.id !== customerId),
                                    "customerName",
                                    "id"
                                )}
                                onChange={(selectedOptions) => {
                                    handleOnChange({
                                        name: "fulfillmentCandidates",
                                        value: selectedOptions
                                    })
                                }}
                                required={true}
                                label={"Fulfillment Candidates"}
                                isMulti={true}
                            />
                        }
                        {customerData?.customerLevel === "CUSTOMER" && customerData?.customerType?.value === "WHOLESALE" &&
                            <InputField
                                name='parentCustomer'
                                label='Is Parent Customer'
                                onClick={handleToggle('parentCustomer')}
                                checked={!!customerData?.parentCustomer}
                                type='checkbox'
                            />
                        }

                        {customerData?.customerType?.value === "RETAIL" &&
                            <>
                                <SelectField
                                    placeholder='Select Parent Customer...'
                                    value={customerData?.parentCustomerReference || null}
                                    options={createOptionListForSelectTag(parentCustomerByTenantQuery?.data?.filter((el: any) => el.id !== customerId), "customerName", "id", "customerNumber")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "parentCustomerReference", value: selectedOption })
                                    }}
                                    label={"Parent Customer"}
                                    isDisabled={!parentCustomerByTenantQuery?.data}
                                />
                                <SelectField
                                    placeholder='Select Parent Customer Region...'
                                    value={customerData?.region || null}
                                    options={createOptionListForSelectTag(regionByCustomerIdQuery?.data, "name", "id")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "region", value: selectedOption })
                                    }}
                                    label={"Parent Customer Region"}
                                    isDisabled={!regionByCustomerIdQuery?.data}
                                />
                            </>
                        }


                        {hasPermission(["PDFSET-U"]) && customerData?.parentCustomerReference && customerData?.customerType?.value === "RETAIL" &&
                            <InputField
                                name={"useParentPdfSettings"}
                                label="Use Parent Pdf Settings"
                                onClick={handleToggle("useParentPdfSettings")}
                                checked={customerData?.useParentPdfSettings || ""}
                                type="toggle"
                                disabled={isReadOnly}
                            />
                        }

                        <InputField
                            name={"requireBuyerAndSellerForNtp"}
                            label="Require buyer/seller for NTP"
                            onClick={handleToggle("requireBuyerAndSellerForNtp")}
                            checked={customerData?.requireBuyerAndSellerForNtp || ""}
                            type="toggle"
                            disabled={isReadOnly}

                        />
                        <InputField
                            name={"alwaysIncludeCurrentYearBills"}
                            label="Always include current year bills"
                            onClick={handleToggle("alwaysIncludeCurrentYearBills")}
                            checked={customerData?.alwaysIncludeCurrentYearBills || ""}
                            type="toggle"
                            disabled={isReadOnly}

                        />
                        <InputField
                            name={"deliverStatementOfAccountSeparately"}
                            label="Deliver SOA separately"
                            onClick={handleToggle("deliverStatementOfAccountSeparately")}
                            checked={customerData?.deliverStatementOfAccountSeparately || ""}
                            type="toggle"
                            disabled={isReadOnly}

                        />

                        <InputField
                            name="dataDeliveryEnabled"
                            label="Deliver data to partners"
                            onClick={handleToggle("dataDeliveryEnabled")}
                            checked={customerData?.dataDeliveryEnabled ?? true}
                            type="toggle"
                            disabled={isReadOnly}
                        />


                        <InputField
                            name={"enabled"}
                            label="Enable Customer"
                            onClick={handleToggle("enabled")}
                            checked={customerData?.enabled || ""}
                            type="toggle"
                            disabled={isReadOnly}

                        />
                        <InputField
                            name={"requireMfa"}
                            label="Require MFA"
                            onClick={handleToggle("requireMfa")}
                            checked={customerData?.requireMfa || ""}
                            type="toggle"
                            disabled={isReadOnly}

                        />


                        {hasPermission(["PDFSET-U"]) && !customerData.useParentPdfSettings &&
                            <div className="col-span-12 mt-8 p-4 rounded-sm border border-text-3 relative"><label
                                className="absolute bg-text-0 -top-3.5 pr-1">PDF Options</label>

                                {customerData?.customerType && customerData.customerType.value === "WHOLESALE" &&
                                    <>
                                        <SelectField
                                            placeholder='Select PDF Logo Option...'
                                            value={pdfSettingData?.pdfHeaderOption || null}
                                            options={createOptionListForSelectTag(enums?.["PdfHeaderOption"], "value", "constant")}
                                            onChange={(selectedOption) => {
                                                handlePdfSettingOnChange({
                                                    name: "pdfHeaderOption",
                                                    value: selectedOption
                                                })
                                            }}
                                            required={true}
                                            label={"PDF Logo Option"}
                                            isDisabled={isReadOnly}

                                        />


                                        {pdfSettingData?.pdfHeaderOption && pdfSettingData.pdfHeaderOption.value === "TITLE" &&
                                            <InputField
                                                name={"pdfHeaderTitle"}
                                                label="PDF Logo Title"
                                                value={pdfSettingData?.pdfHeaderTitle || ""}
                                                type="text"
                                                placeholder="Enter PDF Logo Title..."
                                                onChange={handlePdfSettingOnChange}
                                                disabled={isReadOnly}

                                            />
                                        }

                                        {pdfSettingData?.pdfHeaderOption && pdfSettingData.pdfHeaderOption.value === "LOGO" &&
                                            <div className={`col-span-12 md:grid md:grid-cols-12 gap-3 mt-3`}>
                                                <div className="col-span-3 sm:mt-2 lg-mt-0 mt-2 flex items-center">
                                                    <h2>PDF Logo</h2>
                                                    {pdfSettingData?.pdfLogoPath ?
                                                        <>
                                                            <p className="label-color bold mb-0 ml-2 mr-2 text-highlight-0 cursor-pointer"
                                                                title={pdfSettingData?.pdfLogoPath}
                                                                onClick={() => {
                                                                    handleLogoClick(pdfImageByNameIdQuery?.data?.id)
                                                                }}> (Current Logo)
                                                            </p>

                                                            <ModalButton
                                                                triggerButton={
                                                                    <FaTrash
                                                                        className={"cursor-pointer text-highlight-0 text-lg hover:text-highlight-7"}
                                                                        title={"Remove Current Logo"} />
                                                                }
                                                                onOpen={() => {
                                                                }}
                                                                modal={
                                                                    <ConfirmationModal
                                                                        show={true}
                                                                        setShow={() => {
                                                                        }}
                                                                        title={"Remove Current Logo"}
                                                                        bodyMessage={"Are you sure you want to remove current logo ?"}
                                                                        onSubmit={() => {
                                                                            setPdfSettingData((prevData: any) => ({
                                                                                ...prevData,
                                                                                ["pdfLogoPath"]: null
                                                                            }));
                                                                            setRender(new Date().getMilliseconds());
                                                                        }}
                                                                        isLoading={false}
                                                                    />
                                                                }
                                                            />
                                                        </> : <p className="label-color bold mb-0 ml-2 mr-2 text-highlight-0"> (No Logo)</p>
                                                    }
                                                </div>
                                                <div className="col-span-9">
                                                    <input
                                                        className={"w-full bg-text-0 rounded-md border border-surface-5 form_control p-2"}
                                                        onChange={selectedFile}
                                                        name={"file"}
                                                        type="file"
                                                        accept=".jpg,.jpeg,.png,.svg,.webp,image/*"
                                                        required={customerData?.customerType === 'TENANT'}
                                                    />
                                                </div>
                                            </div>
                                        }

                                        {
                                            pdfSettingData.showRemitName && <InputField
                                                name={"remitName"}
                                                label="Remit Name"
                                                onChange={handlePdfSettingOnChange}
                                                value={pdfSettingData?.remitName || ""}
                                                type="text"
                                                placeholder="Enter Remit Name..."
                                                disabled={isReadOnly}

                                            />
                                        }

                                        {pdfSettingData.showRemitEmail && <InputField
                                            name={"remitEmail"}
                                            label="Remit Email"
                                            onChange={handlePdfSettingOnChange}
                                            value={pdfSettingData?.remitEmail || ""}
                                            type="text"
                                            placeholder="Enter Remit Email..."
                                            disabled={isReadOnly}

                                        />}

                                        {pdfSettingData.showRemitPhone && <InputField
                                            name={"remitPhone"}
                                            label="Remit Phone"
                                            onChange={handlePdfSettingOnChange}
                                            value={pdfSettingData?.remitPhone || ""}
                                            type="text"
                                            placeholder="Enter Remit Phone..."
                                            disabled={isReadOnly}

                                        />}

                                        {pdfSettingData.showRemitAddress && <InputField
                                            name={"remitAddress"}
                                            label="Remit Address"
                                            onChange={handlePdfSettingOnChange}
                                            value={pdfSettingData?.remitAddress || ""}
                                            type="text"
                                            placeholder="Enter Remit Address..."
                                            disabled={isReadOnly}

                                        />}


                                    </>
                                }

                                <InputField
                                    name={"showCustomerName"}
                                    label="Show Customer Name"
                                    onClick={handlePdfSettingToggle("showCustomerName")}
                                    checked={pdfSettingData?.showCustomerName ?? true}
                                    type="toggle"
                                    disabled={isReadOnly}
                                />

                                <InputField
                                    name={"showRemitName"}
                                    label="Show Remit Name"
                                    onClick={handlePdfSettingToggle("showRemitName")}
                                    checked={pdfSettingData?.showRemitName || ""}
                                    type="toggle"
                                    disabled={isReadOnly}

                                />

                                <InputField
                                    name={"showRemitEmail"}
                                    label="Show Remit Email"
                                    onClick={handlePdfSettingToggle("showRemitEmail")}
                                    checked={pdfSettingData?.showRemitEmail || ""}
                                    type="toggle"
                                    disabled={isReadOnly}

                                />

                                <InputField
                                    name={"showRemitPhone"}
                                    label="Show Remit Phone"
                                    onClick={handlePdfSettingToggle("showRemitPhone")}
                                    checked={pdfSettingData?.showRemitPhone || ""}
                                    type="toggle"
                                    disabled={isReadOnly}

                                />

                                <InputField
                                    name={"showRemitAddress"}
                                    label="Show Remit Address"
                                    onClick={handlePdfSettingToggle("showRemitAddress")}
                                    checked={pdfSettingData?.showRemitAddress || ""}
                                    type="toggle"
                                    disabled={isReadOnly}

                                />



                                <InputField
                                    name={"showAlternateContactInfoTable"}
                                    label="Show Alternate Contact Info"
                                    onClick={handlePdfSettingToggle("showAlternateContactInfoTable")}
                                    checked={pdfSettingData?.showAlternateContactInfoTable || ""}
                                    type="toggle"
                                    disabled={isReadOnly}

                                />

                                <InputField
                                    name={"showExemptionInfoTable"}
                                    label="Show Exemption Info Table"
                                    onClick={handlePdfSettingToggle("showExemptionInfoTable")}
                                    checked={pdfSettingData?.showExemptionInfoTable || ""}
                                    type="toggle"
                                    disabled={isReadOnly}

                                />

                                <InputField
                                    name={"showDisclaimerSection"}
                                    label="Show Disclaimer Section"
                                    onClick={handlePdfSettingToggle("showDisclaimerSection")}
                                    checked={pdfSettingData?.showDisclaimerSection || ""}
                                    type="toggle"
                                    disabled={isReadOnly}

                                />



                                <InputField
                                    name={"showWithExemptionEstimate"}
                                    label="Show With Exemption Estimate"
                                    onClick={handlePdfSettingToggle("showWithExemptionEstimate")}
                                    checked={pdfSettingData?.showWithExemptionEstimate || ""}
                                    type="toggle"
                                    disabled={isReadOnly}

                                />
                                {pdfSettingData?.showDisclaimerSection &&
                                    
                                    <TextAreaField
                                    label="Disclaimer"
                                    name="disclaimer"
                                    value={pdfSettingData?.disclaimer || ""}
                                    className="mb-2 text-surface-2 font-normal"
                                    onChange={handlePdfSettingOnChange}
                                    labelClassName={'font-normal'}
                                    disabled={isReadOnly}
                                />
                                }

                            </div>
                        }

                    </div>


                    {!isReadOnly && <div className={"mt-4 flex"}>
                        <ButtonTray
                            buttons={[
                                {
                                    buttonProps: {
                                        btnText: "Cancel",
                                        onClick: () => navigate(AppRoutes.Customers.path),
                                        type: "cancel",
                                    },
                                    buttonType: "button"
                                },
                                {
                                    buttonProps: {
                                        btnText: "Update",
                                        type: "submit",

                                    },
                                    buttonType: "button",
                                }
                            ]}
                            align="start"
                            gap={2}
                        />
                    </div>}

                </div>
            } />

            <Tabs tabs={bottomTabs} className="bootstrap-tabs-component mt-5" />

            {
                showConfirmation ?
                    <ConfirmationModal title={"Update Customer"}
                        bodyMessage={"Are you sure you want to update this customer?"}
                        onSubmit={handleSubmit} show={showConfirmation}
                        setShow={setShowConfirmation}
                        isLoading={updateCustomerApiResponse.isLoading || updatePdfSettingsApiResponse.isLoading || updatePdfLogoApiResponse.isLoading}
                    /> : <></>
            }
            {
                showRemoveLogoConfirmation ?
                    <ConfirmationModal title={"Remove Current Logo"}
                        bodyMessage={"Are you sure you want to remove current logo?"}
                        onSubmit={removeCurrentLogo} show={showRemoveLogoConfirmation}
                        setShow={setShowRemoveLogoConfirmation}

                    /> : <></>
            }
        </>
    )
}
export default SettingTab;
